<template>
  <div id="otherIncome-create" class="group-setup">
    <v-progress-linear
      indeterminate
      color="blue"
      v-if="loading"
    ></v-progress-linear>

    <v-layout row v-if="errors">
      <v-flex>
        <Alert @dismissed="onDismissed" :text="errors"></Alert>
      </v-flex>
    </v-layout>

    <v-form v-if="!loading">
      <v-row>
        <v-col cols="12" md="12" sm="12">
          <v-card class="pa-5" flat>
            <HeaderDividerBlock
              headerText="Add Income Entry"
            ></HeaderDividerBlock>
            <v-row>
              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Income Type</p>
                </v-col>
                <v-autocomplete
                  outlined
                  dense
                  v-model="form.income_type_id"
                  required
                  clearable
                  placeholder="Select Income Type"
                  :items="incomeTypes"
                  item-text="name"
                  item-value="id"
                ></v-autocomplete>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Amount</p>
                </v-col>
                <v-text-field
                  outlined
                  dense
                  v-model="form.amount"
                  required
                  clearable
                  type="number"
                  placeholder="Enter Amount"
                ></v-text-field>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Date of Income</p>
                </v-col>
                <DatePicker
                  v-model="form.date_of_income"
                  placeholder="Date"
                  :max="new Date().toISOString().substr(0, 10)"
                  :rules="[dateValidation]"
                ></DatePicker>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Payer/Source</p>
                </v-col>
                <v-text-field
                  outlined
                  dense
                  v-model="form.payer_source"
                  clearable
                  placeholder="Payer/Source"
                ></v-text-field>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Description/Notes</p>
                </v-col>
                <v-textarea
                  outlined
                  dense
                  v-model="form.description"
                  clearable
                  placeholder="Description"
                ></v-textarea>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Payment Method</p>
                </v-col>
                <v-select
                  outlined
                  dense
                  v-model="form.payment_method"
                  required
                  clearable
                  placeholder="Select Payment Method"
                  :items="['Cash', 'Bank Transfer']"
                ></v-select>
              </v-col>

              <v-col cols="6" sm="6">
                <v-col class="pa-0 ma-0">
                  <p class="text-title">Optional Attachment</p>
                </v-col>
                <v-file-input
                  outlined
                  dense
                  v-model="form.attachment"
                  clearable
                  placeholder="Upload Attachment"
                  accept=".jpg, .jpeg, .png, .pdf"
                ></v-file-input>
              </v-col>
            </v-row>

            <!-- Submit Button -->
            <v-card-actions class="mt-10">
              <v-row>
                <v-col cols="12" md="10" xs="6"></v-col>
                <v-col cols="12" md="2" xs="6">
                  <v-btn
                    v-if="hasPermission('income_entries.create')"
                    large
                    color="#00B3FF"
                    dark
                    depressed
                    block
                    @click="submit"
                  >
                    Add Income Entry
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-form>

    <Snackbars
      :text="succesMsg"
      :snackbar="snackbar"
      :color="'success'"
      @classSnackbar="clossSnackbar"
    />
  </div>
</template>

<script>
import Alert from "@/components/shared/Alert";
import Snackbars from "@/components/shared/Snackbars";
import HeaderDividerBlock from "@/components/shared/HeaderDividerBlock";
import { OtherIncomeService } from "@/services/otherIncome-service.js";
import { checkError } from "@/config/errors.js";
import DatePicker from "@/components/shared/DatePicker";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  name: "LogOtherIncome",
  components: {
    Alert,
    Snackbars,
    HeaderDividerBlock,
    DatePicker,
  },
  data: () => ({
    loading: false,
    errors: null,
    snackbar: false,
    succesMsg: "",
    search: "",
    form: {
      income_type_id: "",
      amount: "",
      date_of_income: "",
      payer_source: "",
      description: "",
      payment_method: "",
      attachment: null,
    },
    incomeTypes: [],
  }),
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
  },
  created() {
    this.fetchIncomeTypes();
  },

  methods: {
    async submit() {
      this.loading = true;
      this.errors = null;

      const formData = new FormData();

      // Append fields to formData, excluding empty/null fields
      Object.entries(this.form).forEach(([key, value]) => {
        if (value !== null && value !== "") {
          formData.append(key, value);
        }
      });

      try {
        const response = await OtherIncomeService.logIncomeEntry(formData);
        this.succesMsg = response.message;
        this.snackbar = true;
        this.resetForm();
      } catch (error) {
        console.error(error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.form = {
        income_type_id: "",
        amount: "",
        date_of_income: "",
        payer_source: "",
        description: "",
        payment_method: "",
        attachment: null,
      };
    },
    async fetchIncomeTypes() {
      try {
        const types = await OtherIncomeService.getIncomeAccountList(
          this.search
        );
        this.incomeTypes = types.data.data;
      } catch (error) {
        console.error(error);
        let data = error.response.data;
        let errorOutput = checkError.errorFormatting(data);
        this.errors = errorOutput;
      }
    },
  },
};
</script>
